import { motion } from "framer-motion";
import { Palette, Film, Video, UserCircle2, Disc3, Megaphone, Globe } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";

const S = [
  { k: "graphic", icon: Palette, t: "Graphic Art", d: "Original gothic, arcane and street-luxe illustrations — rich detail, bold lighting, and a style nobody else has.", span: "md:col-span-2 md:row-span-2", img: "/art/vamp-arcane.jpg" },
  { k: "i2v", icon: Film, t: "Image to Video Art", d: "I take your still image and bring it to life — moving light, smoke, magic and motion.", span: "md:col-span-2" },
  { k: "pfp", icon: UserCircle2, t: "Profile Pictures", d: "Standout PFPs that make people stop scrolling.", span: "" },
  { k: "cover", icon: Disc3, t: "Cover Art", d: "Album, single and mixtape covers with presence.", span: "" },
  { k: "videos", icon: Video, t: "Videos", d: "Edits and visual pieces for your channels.", span: "" },
  { k: "ads", icon: Megaphone, t: "Advertisements", d: "Ads built to get your brand noticed.", span: "" },
  { k: "web", icon: Globe, t: "Websites", d: "Clean, modern websites that make your business look legit and take it to the next level.", span: "md:col-span-2" },
];

const Card = ({ s, i }) => {
  const I = s.icon;
  return (
    <motion.article
      data-testid={`service-card-${s.k}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={`group relative overflow-hidden border border-[var(--line)] bg-[var(--crypt)] p-7 flex flex-col justify-end min-h-[220px] hover:border-[var(--gold)]/70 transition-colors duration-500 ${s.span}`}
    >
      {s.img && (
        <>
          <img src={s.img} alt="Arcane vampire king artwork" className="absolute inset-0 w-full h-full object-cover object-[50%_25%] opacity-70 transition-transform duration-1000 group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--void)] via-[var(--void)]/60 to-transparent" />
        </>
      )}
      <div className="absolute top-0 right-0 w-40 h-40 bg-[var(--blood)]/0 group-hover:bg-[var(--blood)]/20 blur-3xl transition-colors duration-700" />
      <div className="relative">
        <div className="flex items-center justify-between mb-auto">
          <I className="w-7 h-7 text-[var(--gold)] transition-transform duration-500 group-hover:rotate-12" />
          <span className="font-mono text-xs text-[var(--ink-dim)]">0{i + 1}</span>
        </div>
        <h3 className={`font-display font-bold mt-8 ${s.img ? "text-4xl sm:text-5xl" : "text-2xl"}`}>{s.t}</h3>
        <p className="mt-3 text-sm sm:text-base text-[var(--ink-dim)] leading-relaxed max-w-md">{s.d}</p>
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative max-w-[1400px] mx-auto px-5 sm:px-10 py-28 sm:py-40">
    <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
      <Reveal className="lg:col-span-7">
        <Eyebrow n="01">Services</Eyebrow>
        <SectionTitle>Every visual your <span className="italic text-gilded">brand</span> needs.</SectionTitle>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5">
        <p className="text-[var(--ink-dim)] text-base sm:text-lg leading-relaxed">
          From a single profile picture to a full website, every piece is made by hand with care and a professional finish.
        </p>
      </Reveal>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[240px] gap-4">
      {S.map((s, i) => <Card key={s.k} s={s} i={i} />)}
    </div>
  </section>
);
